import { Typography } from '@workspace/ui/components/Typography';
import { useBreakpoint } from '@workspace/ui/hooks/useBreakpoint';
import { Star16 } from '@workspace/ui/icons';
import { cn } from '@workspace/ui/lib/utils';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import Icon from '~/components/Icon/Icon';
import type {
  MultiBuyInfo,
  ProductCardVariant,
} from '~/components/ProductCardV3/types';

import styles from './ProductInfo.module.scss';

/**
 * Props for ProductInfo shared component
 */
export interface ProductInfoProps {
  /**
   * Product name
   */
  name: string;

  /**
   * Product volume/size (formatted string like "750 ml")
   */
  volume: string;

  /**
   * Product rating (0-5)
   */
  rating?: number;

  /**
   * Total number of reviews
   */
  reviewsCount?: number;

  /**
   * Show rating display
   */
  isRatingShown?: boolean;

  /**
   * Card size (determines text sizing)
   */
  size: ProductCardVariant;

  /**
   * Color variants for product (if applicable)
   */
  colorVariants?: Array<{
    color: string;
    sku: string;
    name: string;
  }>;

  /**
   * Callback when variant is selected
   */
  onVariantSelect?: (sku: string) => void;

  /**
   * Product slug for link
   */
  productSlug: string;

  /**
   * Current offer SKU
   */
  currentSku: string;

  /**
   * Multibuy discount information (if applicable)
   */
  multiBuyInfo?: MultiBuyInfo | null;

  /**
   * Number of items in cart
   */
  cartItemsCount?: number;

  /**
   * Required quantity for promo
   */
  promoRequiredQuantity?: number;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Transparent state - card becomes transparent
   */
  transparent?: boolean;
}

const Text400Font = company.match({
  mcosmetic: Typography.Text400Medium,
  default: Typography.Text400Regular,
});

/**
 * Shared component for product info block (name, volume, rating)
 * Display rules:
 * - Size M: name (2 lines max), volume, rating (if isRatingShown=true)
 * - Size S: name (2 lines max), volume (no rating)
 * - Size XS: name only (1 line max, ellipsis)
 */
const ProductInfo = ({
  name,
  volume,
  rating,
  reviewsCount,
  isRatingShown = false,
  size,
  colorVariants,
  onVariantSelect,
  productSlug,
  currentSku,
  multiBuyInfo,
  cartItemsCount = 0,
  promoRequiredQuantity,
  transparent = false,
  className,
}: ProductInfoProps) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();

  const shouldShowRating =
    size === 'm' ? isRatingShown && rating : size === 'l' ? true : false;
  const shouldShowVolume = !!volume;
  const shouldShowMultiBuy =
    size === 'm' &&
    multiBuyInfo?.remainingCount &&
    multiBuyInfo?.nextDiscountPercent;
  const shouldShowReviews =
    (size === 's' || size === 'm') &&
    company.useReviewsOnProductPage &&
    !!rating &&
    !!reviewsCount;

  const shouldShowAddInfo =
    shouldShowVolume || shouldShowMultiBuy || shouldShowReviews;

  const titleComponentMap: Record<
    typeof size,
    { mobile: React.ElementType; desktop: React.ElementType }
  > = {
    l: { mobile: Typography.Text200Medium, desktop: Text400Font },
    xl: { mobile: Typography.Text200Medium, desktop: Text400Font },
    m: { mobile: Typography.Text200Medium, desktop: Text400Font },
    s: {
      mobile: company.match({
        mcosmetic: Typography.Text100Medium,
        default: Typography.Text100Regular,
      }),
      desktop: Text400Font,
    },
    xs: {
      mobile: Typography.Text50Regular,
      desktop: Typography.Text300Regular,
    },
    c: { mobile: Typography.Text200Medium, desktop: Text400Font },
    horizontal: { mobile: Typography.Text200Medium, desktop: Text400Font },
  };

  const TitleComponent = isMobile
    ? titleComponentMap[size].mobile
    : titleComponentMap[size].desktop;

  const addInfoDivider = (
    <span
      className={cn(
        styles.volumeDivider,
        transparent && 'text-graphic-transparent-white-55!',
      )}
    >
      •
    </span>
  );

  return (
    <div
      className={cn(styles.infoContainer, styles[`size_${size}`], className)}
      data-company={company.name}
    >
      <Link
        to={`/p/${productSlug}`}
        state={{ selectedOffer: currentSku }}
        className={styles.nameLink}
        data-link={`product-name-${productSlug}`}
      >
        <TitleComponent
          as="h3"
          className={cn(
            styles.name,
            transparent && 'text-text-inverse-primary!',
          )}
        >
          {name}
        </TitleComponent>
      </Link>

      {shouldShowAddInfo && (
        <div className={styles.volumeRow}>
          {shouldShowReviews && (
            <span
              className={cn(
                styles.reviewsInfo,
                transparent && 'text-graphic-transparent-white-55!',
              )}
            >
              <Star16 />
              {rating.toFixed(1)} ({reviewsCount})
            </span>
          )}

          {shouldShowVolume && (
            <>
              {shouldShowReviews && addInfoDivider}
              <p
                className={cn(
                  styles.volume,
                  styles[`volume_size_${size}`],
                  transparent && 'text-graphic-transparent-white-55!',
                )}
              >
                {volume}
              </p>
            </>
          )}

          {!!shouldShowMultiBuy && (
            <>
              {addInfoDivider}
              <span className={styles.multiBuyText}>
                {cartItemsCount === 0 && promoRequiredQuantity
                  ? t('multibuyOfferNoCartValue', {
                      count: promoRequiredQuantity,
                      value: multiBuyInfo.discountValue,
                    })
                  : multiBuyInfo.remainingCount &&
                      multiBuyInfo.nextDiscountPercent
                    ? t('multibuyOfferWithCartValue', {
                        count: Math.max(0, multiBuyInfo.remainingCount),
                        value: multiBuyInfo.nextDiscountValue,
                      })
                    : null}
              </span>
            </>
          )}
        </div>
      )}

      {shouldShowRating && (
        <div className={styles.ratingContainer}>
          <Icon
            type={
              company.name === CompanyName.DubaiExpress
                ? 'camel-rating'
                : size === 'l'
                  ? 'star'
                  : 'star-fill'
            }
            size={14}
            className={cn(
              styles.starIcon,
              size === 'l' && styles.starIcon_size_l,
            )}
          />
          <span
            className={cn(
              styles.ratingValue,
              size === 'l' && styles.ratingValue_size_l,
              transparent && 'text-graphic-transparent-white-55!',
            )}
          >
            {(rating ?? 0).toFixed(1)}
          </span>
        </div>
      )}

      {colorVariants && colorVariants.length > 1 && size === 'm' && (
        <div className={styles.colorVariantsContainer}>
          {colorVariants.map((variant) => (
            <button
              key={variant.sku}
              type="button"
              className={cn(
                styles.colorVariant,
                variant.sku === currentSku && styles.colorVariant_active,
              )}
              onClick={() => onVariantSelect?.(variant.sku)}
              aria-label={variant.name}
              style={{ backgroundColor: variant.color }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductInfo;
